// Browser side of PDF export. The browser never talks to the worker: it calls
// the docgen-export edge function with the admin's session, and that function
// (admin check + worker secret) does the rest. See
// supabase/functions/docgen-export/index.ts and docgen-worker/server.mjs.

import { sb, SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY } from '../../supabase';
import { pdfFileName } from './render.js';

async function callExport(documentId, mode) {
  const { data: { session } } = await sb.auth.getSession();
  if (!session) throw new Error('not signed in');
  const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/docgen-export`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ document_id: documentId, mode }),
  });
  if (!res.ok) {
    let detail = {};
    try { detail = await res.json(); } catch { detail = { error: `HTTP ${res.status}` }; }
    console.error('[docgen] export failed:', res.status, detail.error);
    const err = new Error(detail.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.errors = detail.errors || [];
    throw err;
  }
  return res;
}

/** A DRAFT-marked proof. Nothing is stored and no number is assigned. */
export async function draftPdf(documentId) {
  const res = await callExport(documentId, 'draft');
  return { blob: await res.blob(), warning: res.headers.get('x-pdf-warning') };
}

/** Approve: number assigned, PDF rendered and stored, document locked. */
export async function approveAndExport(documentId) {
  const res = await callExport(documentId, 'final');
  return res.json(); // { ref_no, pdf_path, file_name, sha256, bytes, warn }
}

/** A short-lived link to the stored PDF; the download is logged. */
export async function downloadStoredPdf(doc) {
  if (!doc.pdf_path) throw new Error('no stored PDF');
  const { data, error } = await sb.storage.from('doc-files')
    .createSignedUrl(doc.pdf_path, 300, { download: pdfFileName(doc) });
  if (error) {
    console.error('[docgen] signed PDF url:', error.message);
    throw error;
  }
  const { data: { user } } = await sb.auth.getUser();
  const { error: logErr } = await sb.from('doc_exports')
    .insert({ document_id: doc.id, action: 'downloaded', pdf_path: doc.pdf_path, pdf_sha256: doc.pdf_sha256, actor: user?.id });
  if (logErr) console.error('[docgen] download log:', logErr.message); // never block the download on the log
  return data.signedUrl;
}
